/**
 * Pagina BI, layout a due zone (Card): "Domanda" e "Risultato".
 * Solo impaginazione: la logica (useBiAsk, stati, handler) e i sotto-componenti
 * (AskBox, ChartTypeSwitch, ResultChart, ResultTable) sono INVARIATI.
 *
 * Due canali di errore (invariati):
 *  - mutation.error    -> trasporto/auth (axios lancia: 401, 500, rete)
 *  - data.ok === false -> errore "business" (HTTP 200 con phase/message)
 */

import { useEffect, useState } from 'react'
import { Alert, Card, Space, Spin, Typography } from 'antd'

import AskBox from './components/AskBox'
import ChartTypeSwitch from './components/ChartTypeSwitch'
import ResultChart from './components/ResultChart'
import ResultTable from './components/ResultTable'
import { enabledChartTypes, suggestChartType } from './chartHeuristic'
import type { ChartType } from './chartHeuristic'
import { useBiAsk } from './useBiAsk'

function BIPage() {
  const bi = useBiAsk()
  const [chartType, setChartType] = useState<ChartType>('table')

  // Ad ogni NUOVA risposta di successo, reimposta il tipo suggerito.
  // Dipende solo da bi.data → nessun loop (setChartType non modifica bi.data).
  useEffect(() => {
    if (bi.data && bi.data.ok) {
      setChartType(suggestChartType(bi.data.columns, bi.data.data))
    }
  }, [bi.data])

  const handleAsk = (question: string) => {
    bi.mutate({ question })
  }

  const transportError =
    bi.error instanceof Error ? bi.error.message : bi.error != null ? String(bi.error) : null

  return (
    <div style={{ padding: 16 }}>
      {/* Zona 1 — Domanda */}
      <Card size="small">
        <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 12 }}>
          Analisi (BI)
        </Typography.Title>
        <AskBox onAsk={handleAsk} loading={bi.isPending} />
      </Card>

      {/* Stati intermedi: al posto della zona Risultato */}
      {bi.isPending && (
        <div style={{ marginTop: 16 }}>
          <Spin /> <span>Sto interrogando i dati… (può richiedere qualche secondo)</span>
        </div>
      )}

      {!bi.isPending && transportError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Errore di rete o autenticazione"
          description={transportError}
        />
      )}

      {!bi.isPending && bi.data && !bi.data.ok && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message={`Non sono riuscito a rispondere (fase: ${bi.data.phase})`}
          description={bi.data.message}
        />
      )}

      {/* Zona 2 — Risultato (solo se ok) */}
      {!bi.isPending && bi.data && bi.data.ok && (
        <Card size="small" style={{ marginTop: 16 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ChartTypeSwitch
              value={chartType}
              onChange={setChartType}
              enabled={enabledChartTypes(bi.data.columns, bi.data.data)}
            />
            <ResultChart columns={bi.data.columns} data={bi.data.data} type={chartType} />
            <ResultTable columns={bi.data.columns} data={bi.data.data} />
          </Space>
        </Card>
      )}
    </div>
  )
}

export default BIPage
